import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from 'antd';
import { DocumentDownload } from 'iconsax-react';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import Helpers from '@/lib/helpers';
import { ProductCustomizationData } from '@/types/logisticsFeedback';

export type { ProductCustomizationData };

export interface FeedbackProductCustomizationProps {
  customization?: ProductCustomizationData;
  imageUrl?: string;
  dataTestIdPrefix: string;
  containerClassName?: string;
}

export const FeedbackProductCustomization: React.FC<FeedbackProductCustomizationProps> = ({
  customization,
  imageUrl,
  dataTestIdPrefix,
  containerClassName = 'space-y-3',
}) => {
  const { t } = useTranslation();

  if (!customization) return null;

  const logoData = customization.logo;
  const privateLabel = customization.privateLabel;
  const colorData = customization.color;
  const packaging = customization.packaging;

  if (!logoData && !privateLabel && !colorData && !packaging) return null;

  const downloadUrl = logoData?.fileUrl || logoData?.imageUrl || imageUrl;

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloadUrl) {
      Helpers.downloadImage(downloadUrl, logoData?.name || 'custom-logo');
    }
  };

  return (
    <div
      className={containerClassName}
      data-testid={`${dataTestIdPrefix}-wrapper`}
    >
      <TitleText
        text={t('logisticsFeedback.customization')}
        size="base"
        className="!font-bold !text-light-primary dark:!text-dark-primary mb-3"
        data-testid={`customization-title-${dataTestIdPrefix}`}
      />

      {/* Custom logo */}
      {logoData && (
        <div
          className="flex items-center justify-between py-1"
          data-testid={`customization-logo-row-${dataTestIdPrefix}`}
        >
          <InfoText
            text={t('logisticsFeedback.customLogo')}
            className="!text-sm !text-light-text-secondary dark:!text-dark-text-secondary"
            data-testid={`customization-logo-label-${dataTestIdPrefix}`}
          />
          <div
            className="flex items-center gap-2"
            data-testid={`customization-logo-preview-${dataTestIdPrefix}`}
          >
            {imageUrl && (
              <Image
                src={imageUrl}
                alt="logo thumbnail"
                width={28}
                height={28}
                className="rounded-lg border border-light-input-border dark:border-dark-input-border object-cover"
              />
            )}
            <InfoText
              text={logoData.name || '-'}
              className="!text-sm !font-medium !text-light-primary dark:!text-dark-primary"
              data-testid={`customization-logo-filename-${dataTestIdPrefix}`}
            />
            {downloadUrl && (
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center justify-center p-0.5 hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-0"
                aria-label={t('download') || 'Download'}
                data-testid={`customization-logo-download-${dataTestIdPrefix}`}
              >
                <DocumentDownload
                  size={18}
                  className="text-emerald-500 shrink-0"
                />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Private Label (Striped soft background) */}
      {privateLabel && (
        <div
          className="flex items-center justify-between p-3 rounded-lg bg-soft-background dark:bg-dark-surface-secondary"
          data-testid={`customization-private-label-row-${dataTestIdPrefix}`}
        >
          <InfoText
            text={t('logisticsFeedback.privateLabel')}
            className="!text-sm !text-light-text-secondary dark:!text-dark-text-secondary"
            data-testid={`customization-private-label-text-${dataTestIdPrefix}`}
          />
          <InfoText
            text={privateLabel}
            className="!text-sm !font-semibold !text-light-primary dark:!text-dark-primary"
            data-testid={`customization-private-label-value-${dataTestIdPrefix}`}
          />
        </div>
      )}

      {/* Custom Color */}
      {colorData && (
        <div
          className="flex items-center justify-between py-1 px-1"
          data-testid={`customization-color-row-${dataTestIdPrefix}`}
        >
          <InfoText
            text={t('logisticsFeedback.customColor')}
            className="!text-sm !text-light-text-secondary dark:!text-dark-text-secondary"
            data-testid={`customization-color-label-${dataTestIdPrefix}`}
          />
          <div
            className="flex items-center gap-2"
            data-testid={`customization-color-badge-${dataTestIdPrefix}`}
          >
            {colorData.colorCode && (
              <div
                className={`w-5 h-5 rounded ${colorData.colorCode} border border-light-input-border dark:border-dark-input-border shrink-0`}
              />
            )}
            <InfoText
              text={colorData.name || '-'}
              className="!text-sm !font-semibold !text-light-primary dark:!text-dark-primary"
              data-testid={`customization-color-value-${dataTestIdPrefix}`}
            />
          </div>
        </div>
      )}

      {/* Custom Packaging (Striped soft background) */}
      {packaging && (
        <div
          className="flex items-center justify-between p-3 rounded-lg bg-soft-background dark:bg-dark-surface-secondary"
          data-testid={`customization-packaging-row-${dataTestIdPrefix}`}
        >
          <InfoText
            text={t('logisticsFeedback.customPackaging')}
            className="!text-sm !text-light-text-secondary dark:!text-dark-text-secondary"
            data-testid={`customization-packaging-label-${dataTestIdPrefix}`}
          />
          <InfoText
            text={packaging}
            className="!text-sm !font-semibold !text-light-primary dark:!text-dark-primary"
            data-testid={`customization-packaging-value-${dataTestIdPrefix}`}
          />
        </div>
      )}
    </div>
  );
};
